/**
 * Staff review queue for prescriptions uploaded through the patient portal.
 *
 * Replaces the old Care-module request view for this tab (which only ever
 * showed `care_version` records — none of which patients can actually
 * create) with one that reads the real upload collection via
 * `/staff/portal/prescriptions`. Every upload is first run through the
 * existing AI prescription scanner (`services/prescription_scan.py`); a
 * failed validation can only be approved by a pharmacist/admin holding the
 * `rx_override` permission, with a documented reason.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, FileText, Image as ImageIcon, ShieldAlert, Sparkles, RefreshCw } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import './care.css';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  scanned: 'bg-sky-100 text-sky-700',
  reviewed: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const canOverride = (user) =>
  ['pharmacist', 'super_admin', 'admin'].includes(user?.role) ||
  (user?.custom_permissions || []).includes('rx_override');

export const StaffRxReviewPanel = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // { rx, action }
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [rescanning, setRescanning] = useState(() => new Set());
  const [opening, setOpening] = useState(() => new Set());

  const load = useCallback(() => {
    setLoading(true);
    api.get('/staff/portal/prescriptions', { params: { status } })
      .then((r) => setRows(r.data.prescriptions || []))
      .catch(() => toast.error('Could not load prescriptions'))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const failedValidation = (rx) => rx?.ai_scan && rx.ai_scan.is_valid === false;
  const openReview = (rx, action) => { setReviewing({ rx, action }); setNote(''); };
  const needsOverride = reviewing?.action === 'approve' && failedValidation(reviewing.rx);
  const blocked = needsOverride && !canOverride(user);

  const decide = async () => {
    if (!reviewing) return;
    setSaving(true);
    try {
      const r = await api.post(`/staff/portal/prescriptions/${reviewing.rx.rx_id}/review`, {
        action: reviewing.action, note: note.trim(),
      });
      toast.success(
        reviewing.action === 'approve'
          ? (r.data.overrode_failed_validation ? 'Approved — AI validation overridden' : 'Prescription approved')
          : 'Prescription rejected',
      );
      setReviewing(null); setNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const retryScan = async (rx) => {
    setRescanning((s) => new Set(s).add(rx.rx_id));
    try {
      const r = await api.post(`/staff/portal/prescriptions/${rx.rx_id}/rescan`);
      setRows((prev) => prev.map((row) => (row.rx_id === rx.rx_id ? r.data : row)));
      toast.success('AI scan complete');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rescan failed');
    } finally {
      setRescanning((s) => { const n = new Set(s); n.delete(rx.rx_id); return n; });
    }
  };

  const viewDocument = async (rx) => {
    const popup = window.open('', '_blank');
    setOpening((s) => new Set(s).add(rx.rx_id));
    try {
      const r = await api.get(`/staff/portal/prescriptions/${rx.rx_id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (popup) popup.location = url; else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (popup) popup.close();
      toast.error(e?.response?.data?.detail || 'Could not open document');
    } finally {
      setOpening((s) => { const n = new Set(s); n.delete(rx.rx_id); return n; });
    }
  };

  return (
    <div className="space-y-4" data-testid="staff-rx-review-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2>Prescription reviews</h2>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[170px]" data-testid="staff-rx-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending" data-testid="staff-rx-status-pending">Awaiting AI scan</SelectItem>
            <SelectItem value="scanned" data-testid="staff-rx-status-scanned">Awaiting pharmacist</SelectItem>
            <SelectItem value="reviewed" data-testid="staff-rx-status-reviewed">Approved</SelectItem>
            <SelectItem value="rejected" data-testid="staff-rx-status-rejected">Rejected</SelectItem>
            <SelectItem value="all" data-testid="staff-rx-status-all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && <p className="care-empty" data-testid="staff-rx-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}
      {!loading && rows.length === 0 && <p className="care-empty" data-testid="staff-rx-empty">Nothing in this queue.</p>}

      <div className="space-y-3">
        {rows.map((r) => {
          const Icon = (r.content_type || '').startsWith('image/') ? ImageIcon : FileText;
          const scan = r.ai_scan && typeof r.ai_scan === 'object' ? r.ai_scan : null;
          const failed = scan && scan.is_valid === false;
          const scanUnavailable = !scan || !!scan.provider_error;
          const isRescanning = rescanning.has(r.rx_id);
          return (
            <article key={r.rx_id} className="care-record" data-testid={`staff-rx-${r.rx_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex gap-3 items-start min-w-0">
                  <div className="p-2 rounded-lg bg-slate-100 text-slate-600 shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0">
                    <div className="font-medium truncate" data-testid={`staff-rx-patient-${r.rx_id}`}>
                      {r.patient_name} · <span className="text-slate-500 font-normal">{r.original_filename || 'file'}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.patient_phone && <>{r.patient_phone} · </>}{new Date(r.created_at).toLocaleString()}
                    </div>
                    {r.notes && <div className="text-sm text-slate-700 mt-1">{r.notes}</div>}
                  </div>
                </div>
                <Badge className={STATUS_BADGE[r.status] || STATUS_BADGE.pending} data-testid={`staff-rx-badge-${r.rx_id}`}>{r.status}</Badge>
              </div>

              {scan && (
                <div
                  className={`mt-3 rounded-lg border p-3 text-sm ${failed ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}
                  data-testid={`staff-rx-scan-${r.rx_id}`}
                >
                  <div className="flex items-center gap-1.5 font-medium">
                    <Sparkles size={14} /> AI scan — {failed ? 'needs pharmacist attention' : 'looks valid'}
                  </div>
                  {!!(scan.medications || []).length && (
                    <p className="mt-1 text-slate-700">{scan.medications.map((m) => m.name).filter(Boolean).join(', ')}</p>
                  )}
                  {!!(scan.errors || []).length && (
                    <ul className="mt-1 list-disc list-inside text-amber-800">
                      {scan.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                  {!!(scan.warnings || []).length && (
                    <ul className="mt-1 list-disc list-inside text-slate-600">
                      {scan.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}
              {scan?.provider_error && (
                <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-600" data-testid={`staff-rx-scan-error-${r.rx_id}`}>
                  <ShieldAlert size={14} className="inline mr-1.5" />
                  Automatic scan was unavailable — you can review the document manually or try again.
                </div>
              )}
              {r.status === 'pending' && !scan && (
                <p className="text-xs text-slate-400 mt-2" data-testid={`staff-rx-scan-pending-${r.rx_id}`}>AI scan running…</p>
              )}
              {r.review?.note && (
                <p className="text-sm border-t pt-2 mt-2 whitespace-pre-wrap" data-testid={`staff-rx-review-note-${r.rx_id}`}>{r.review.note}</p>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  variant="outline" size="sm" onClick={() => viewDocument(r)} disabled={opening.has(r.rx_id)}
                  data-testid={`staff-rx-view-${r.rx_id}`}
                >
                  {opening.has(r.rx_id) ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  View document
                </Button>
                {['pending', 'scanned'].includes(r.status) && scanUnavailable && (
                  <Button
                    variant="outline" size="sm" onClick={() => retryScan(r)} disabled={isRescanning}
                    data-testid={`staff-rx-retry-scan-${r.rx_id}`}
                  >
                    {isRescanning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    {isRescanning ? 'Scanning…' : 'Retry AI scan'}
                  </Button>
                )}
                {['pending', 'scanned'].includes(r.status) && (
                  <>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openReview(r, 'approve')} data-testid={`staff-rx-approve-${r.rx_id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => openReview(r, 'reject')} data-testid={`staff-rx-reject-${r.rx_id}`}>
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o) { setReviewing(null); setNote(''); } }}>
        <DialogContent data-testid="staff-rx-review-dialog" closeProps={{ 'data-testid': 'staff-rx-review-close' }}>
          <DialogHeader>
            <DialogTitle>{reviewing?.action === 'approve' ? 'Approve prescription' : 'Reject prescription'}</DialogTitle>
            <DialogDescription>The patient will be notified of this decision.</DialogDescription>
          </DialogHeader>
          {needsOverride && (
            <p className="care-warning text-sm" role="alert" data-testid="staff-rx-override-warning">
              <ShieldAlert className="w-4 h-4 inline mr-1" />
              This prescription failed automatic validation.{' '}
              {canOverride(user) ? 'Provide a reason to override and approve anyway.' : 'Only a pharmacist or admin can override this.'}
            </p>
          )}
          <Textarea
            rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note the patient will see…" data-testid="staff-rx-review-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewing(null); setNote(''); }}>Cancel</Button>
            <Button
              onClick={decide}
              disabled={saving || blocked || (needsOverride && !note.trim())}
              className={reviewing?.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="staff-rx-review-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
